import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, UserCheck, UserX, UserPlus, Search, RefreshCw, 
  Star, AlertTriangle, TrendingUp, Clock, ShoppingBag,
  ChevronRight, Calendar, MapPin, Phone, Mail, Heart,
  Sparkles, Filter, BarChart3, ArrowUpDown, Eye, Layers
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import GuestSegments from './GuestSegments';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function GuestManager() {
  // State
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [guests, setGuests] = useState([]);
  const [stats, setStats] = useState(null);
  const [overviewStats, setOverviewStats] = useState(null);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [guestOrders, setGuestOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('guests');
  
  // Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('last_order_date');
  const [sortOrder, setSortOrder] = useState('desc');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchGuests = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: '100'
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (filterStatus !== 'all') params.append('filter_status', filterStatus);
      
      const response = await fetch(`${API_BASE}/api/guests/list?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setGuests(data.guests || []);
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Guests fetch error:', error);
    }
  }, [searchQuery, filterStatus, sortBy, sortOrder]);

  const fetchOverviewStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/guests/stats/overview`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setOverviewStats(data);
      }
    } catch (error) {
      console.error('Stats fetch error:', error);
    }
  }, []);

  const fetchGuestDetails = async (guestId) => {
    try {
      const response = await fetch(`${API_BASE}/api/guests/profile/${guestId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedGuest(data.guest);
        setGuestOrders(data.recent_orders || []);
        setShowGuestModal(true);
      }
    } catch (error) {
      console.error('Guest details fetch error:', error);
      toast.error('Fehler beim Laden des Gastprofils');
    }
  };

  const syncGuests = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_BASE}/api/guests/sync`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Synchronisation abgeschlossen', {
          description: data.message
        });
        await fetchGuests();
        await fetchOverviewStats();
      } else {
        toast.error('Synchronisation fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Netzwerkfehler');
    } finally {
      setSyncing(false);
    }
  };

  const seedDemoGuests = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/guests/demo/seed`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Demo-Daten erstellt', {
          description: data.message
        });
        await fetchGuests();
        await fetchOverviewStats();
      }
    } catch (error) {
      toast.error('Fehler beim Erstellen der Demo-Daten');
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchGuests(), fetchOverviewStats()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchGuests, fetchOverviewStats]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchGuests();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, filterStatus, sortBy, sortOrder, fetchGuests]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getChannelIcon = (channel) => {
    switch(channel) {
      case 'delivery': return '🚚';
      case 'pickup': return '🏃';
      case 'dine_in': return '🍽️';
      case 'qr_code': return '📱';
      default: return '📦';
    }
  };

  const getChannelLabel = (channel) => {
    switch(channel) {
      case 'delivery': return 'Lieferung';
      case 'pickup': return 'Abholung';
      case 'dine_in': return 'Vor Ort';
      case 'qr_code': return 'QR-Bestellung';
      default: return 'Unbekannt';
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen" data-testid="guest-manager-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-400">Lade KI-Gastemanager...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="guest-manager-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-purple-400" />
            KI-Gastemanager
          </h1>
          <p className="text-gray-400 mt-1">
            Automatische Gästeanalyse & KI-generierte Profile
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={seedDemoGuests}
            data-testid="seed-demo-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Demo-Daten
          </Button>
          <Button 
            onClick={syncGuests}
            disabled={syncing}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            data-testid="sync-guests-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Synchronisiere...' : 'Gäste synchronisieren'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Gäste gesamt</p>
                  <p className="text-2xl font-bold text-white">{stats.total_guests}</p>
                </div>
                <Users className="w-8 h-8 text-blue-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">VIP-Gäste</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats.vip_count}</p>
                </div>
                <Star className="w-8 h-8 text-yellow-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Abwanderungsrisiko</p>
                  <p className="text-2xl font-bold text-red-400">{stats.at_risk_count}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Neukunden</p>
                  <p className="text-2xl font-bold text-green-400">{stats.new_count}</p>
                </div>
                <UserPlus className="w-8 h-8 text-green-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Ø Warenkorb</p>
                  <p className="text-2xl font-bold text-white">{formatCurrency(stats.avg_order_value)}</p>
                </div>
                <ShoppingBag className="w-8 h-8 text-purple-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-800/50 border border-slate-700 p-1">
          <TabsTrigger value="guests" className="data-[state=active]:bg-blue-600">
            <Users className="w-4 h-4 mr-2" />
            Gästeliste
          </TabsTrigger>
          <TabsTrigger value="segments" className="data-[state=active]:bg-blue-600">
            <Layers className="w-4 h-4 mr-2" />
            KI-Segmente
          </TabsTrigger>
        </TabsList>

        {/* Guests Tab */}
        <TabsContent value="guests" className="space-y-6">
          {/* Filter Bar */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Gäste suchen (Name, E-Mail, Telefon)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700"
                data-testid="guest-search-input"
              />
            </div>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px] bg-slate-800 border-slate-700" data-testid="filter-status-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Gäste</SelectItem>
                <SelectItem value="vip">VIP-Gäste</SelectItem>
                <SelectItem value="at_risk">Abwanderungsgefährdet</SelectItem>
                <SelectItem value="new">Neukunden</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] bg-slate-800 border-slate-700" data-testid="sort-by-select">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sortieren" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last_order_date">Letzte Bestellung</SelectItem>
                <SelectItem value="total_orders">Bestellungen</SelectItem>
                <SelectItem value="total_spent">Umsatz</SelectItem>
                <SelectItem value="name">Name</SelectItem>
              </SelectContent>
            </Select>
          </div>

      {/* Guest List */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5" />
            Gästeliste
            <Badge variant="outline" className="ml-2">{guests.length} Gäste</Badge>
          </CardTitle>
          <CardDescription>
            KI-analysierte Gästeprofile mit automatischen Zusammenfassungen
          </CardDescription>
        </CardHeader>
        <CardContent>
          {guests.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <h3 className="text-lg font-medium text-white mb-2">Keine Gäste gefunden</h3>
              <p className="text-gray-400 mb-4">
                Synchronisieren Sie Ihre Bestellungen, um Gästeprofile zu erstellen
              </p>
              <div className="flex justify-center gap-3">
                <Button onClick={seedDemoGuests} variant="outline">
                  Demo-Daten erstellen
                </Button>
                <Button onClick={syncGuests}>
                  Gäste synchronisieren
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {guests.map((guest, idx) => (
                <div
                  key={guest.guest_id || idx}
                  className="p-4 rounded-lg bg-slate-900/50 border border-slate-700 hover:border-slate-600 transition-colors cursor-pointer"
                  onClick={() => fetchGuestDetails(guest.guest_id)}
                  data-testid={`guest-card-${idx}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {/* Avatar & Status */}
                      <div className="relative">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                          guest.is_vip ? 'bg-yellow-500/20 text-yellow-400' :
                          guest.is_at_risk ? 'bg-red-500/20 text-red-400' :
                          guest.is_new ? 'bg-green-500/20 text-green-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {guest.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        {guest.is_vip && (
                          <Star className="absolute -top-1 -right-1 w-4 h-4 text-yellow-400 fill-yellow-400" />
                        )}
                      </div>
                      
                      {/* Guest Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-white">{guest.name}</h3>
                          {guest.is_vip && (
                            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">VIP</Badge>
                          )}
                          {guest.is_at_risk && (
                            <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Risiko
                            </Badge>
                          )}
                          {guest.is_new && (
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Neu</Badge>
                          )}
                        </div>
                        
                        {/* KI-Zusammenfassung */}
                        <p className="text-sm text-purple-300 mb-2 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          {guest.ai_summary || 'Profil wird analysiert...'}
                        </p>
                        
                        {/* Contact Info */}
                        <div className="flex items-center gap-4 text-xs text-gray-400">
                          {guest.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {guest.email}
                            </span>
                          )}
                          {guest.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {guest.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Metrics */}
                    <div className="flex items-center gap-6 text-right">
                      <div>
                        <p className="text-lg font-bold text-white">{guest.total_orders}</p>
                        <p className="text-xs text-gray-400">Bestellungen</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-white">{formatCurrency(guest.total_spent)}</p>
                        <p className="text-xs text-gray-400">Umsatz</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-white">{formatCurrency(guest.average_order_value)}</p>
                        <p className="text-xs text-gray-400">Ø Warenkorb</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg">{getChannelIcon(guest.preferred_channel)}</p>
                        <p className="text-xs text-gray-400">{getChannelLabel(guest.preferred_channel)}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Segments Tab */}
        <TabsContent value="segments">
          <GuestSegments />
        </TabsContent>
      </Tabs>

      {/* Guest Detail Modal */}
      <Dialog open={showGuestModal} onOpenChange={setShowGuestModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                selectedGuest?.is_vip ? 'bg-yellow-500/20 text-yellow-400' :
                selectedGuest?.is_at_risk ? 'bg-red-500/20 text-red-400' :
                selectedGuest?.is_new ? 'bg-green-500/20 text-green-400' :
                'bg-blue-500/20 text-blue-400'
              }`}>
                {selectedGuest?.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  {selectedGuest?.name}
                  {selectedGuest?.is_vip && (
                    <Badge className="bg-yellow-500/20 text-yellow-400">VIP</Badge>
                  )}
                </div>
                <p className="text-sm text-purple-300 font-normal flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  {selectedGuest?.ai_summary}
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedGuest && (
            <div className="space-y-6 mt-4">
              {/* Contact Info */}
              <div className="flex items-center gap-4 text-sm text-gray-400">
                {selectedGuest.email && (
                  <span className="flex items-center gap-2 bg-slate-800 px-3 py-1.5 rounded-lg">
                    <Mail className="w-4 h-4" />
                    {selectedGuest.email}
                  </span>
                )}
                {selectedGuest.phone && (
                  <span className="flex items-center gap-2 bg-slate-800 px-3 py-1.5 rounded-lg">
                    <Phone className="w-4 h-4" />
                    {selectedGuest.phone}
                  </span>
                )}
              </div>
              
              {/* Key Metrics */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-white">{selectedGuest.total_orders}</p>
                    <p className="text-xs text-gray-400">Bestellungen</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-white">{formatCurrency(selectedGuest.total_spent)}</p>
                    <p className="text-xs text-gray-400">Gesamtumsatz</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-white">{formatCurrency(selectedGuest.average_order_value)}</p>
                    <p className="text-xs text-gray-400">Ø Warenkorb</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-white">{selectedGuest.avg_days_between_orders || 0}</p>
                    <p className="text-xs text-gray-400">Tage zw. Bestellungen</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Detailed Analysis */}
              <div className="grid grid-cols-2 gap-4">
                {/* Lieblingsgerichte */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-white flex items-center gap-2">
                      <Heart className="w-4 h-4 text-red-400" />
                      Lieblingsgerichte
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedGuest.favorite_products?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedGuest.favorite_products.slice(0, 5).map((product, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm text-white">{product.name}</span>
                            <Badge variant="outline">{product.count}x</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">Keine Daten</p>
                    )}
                  </CardContent>
                </Card>
                
                {/* Bestellverhalten */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-white flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-blue-400" />
                      Bestellverhalten
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Bevorzugter Tag</span>
                      <span className="text-white">{selectedGuest.preferred_day || '-'}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Bevorzugte Zeit</span>
                      <span className="text-white">{selectedGuest.preferred_time || '-'}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Wochenend-Anteil</span>
                      <span className="text-white">{selectedGuest.weekend_preference?.toFixed(0) || 0}%</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Kanal</span>
                      <span className="text-white flex items-center gap-1">
                        {getChannelIcon(selectedGuest.preferred_channel)}
                        {getChannelLabel(selectedGuest.preferred_channel)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Price Sensitivity */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    Preis-Sensitivität
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Premium-orientiert</span>
                      <span className="text-gray-400">Preisbewusst</span>
                    </div>
                    <Progress value={selectedGuest.price_sensitivity || 50} className="h-2" />
                    <p className="text-xs text-center text-gray-400">
                      {selectedGuest.price_sensitivity < 40 ? 'Dieser Gast bevorzugt Qualität über Preis' :
                       selectedGuest.price_sensitivity > 60 ? 'Dieser Gast ist preissensibel - Aktionen funktionieren gut' :
                       'Ausgewogenes Preis-Leistungs-Verhältnis'}
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* Timestamps */}
              <div className="flex items-center justify-between text-sm text-gray-400 pt-2 border-t border-slate-700">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Erste Bestellung: {formatDate(selectedGuest.first_order_date)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Letzte Bestellung: {formatDate(selectedGuest.last_order_date)}
                </span>
              </div>
              
              {/* Recent Orders */}
              {guestOrders.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-white">Letzte Bestellungen</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {guestOrders.slice(0, 5).map((order, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm py-2 border-b border-slate-700 last:border-0">
                          <div>
                            <p className="text-white">{formatDate(order.created_at)}</p>
                            <p className="text-xs text-gray-400">
                              {order.items?.length || 0} Artikel
                            </p>
                          </div>
                          <p className="font-medium text-white">
                            {formatCurrency(order.total || order.total_gross || 0)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
